// SCR-STAFF-1002 — فراخوان‌های ناموفق / Dead Letter (US-INT-012؛ AP-INT-02): فهرست با فیلتر وضعیت، ارسال مجدد (با همان
// Idempotency-Key فراخوان اصلی در سرور) و «رها کردن» با دلیل الزامی (PHX-INT-0109)؛ موارد حل‌شده فقط‌خواندنی (PHX-INT-0108).
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { unwrap, type DeadLetterView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  ListPage,
  ReasonDialog,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { AdapterName, intKeys } from './common';

export const SCR_STAFF_1002 = 'SCR-STAFF-1002';
const STATUSES = ['Open', 'Resent', 'Abandoned', 'All'] as const;
type DeadLetterFilter = (typeof STATUSES)[number];

export function DeadLettersPage() {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('int.dead_letter.manage');
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<DeadLetterFilter>('Open');
  const [abandoning, setAbandoning] = useState<DeadLetterView | null>(null);
  const [notice, setNotice] = useState(false);

  const letters = useQuery({
    queryKey: intKeys.deadLetters(session?.tenantId, status),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/integration/dead-letters', { params: { query: { status, limit: 100 } } })),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['integration', 'dead-letters'] });

  const resend = useMutation({
    mutationFn: (letter: DeadLetterView) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/dead-letters/{id}:resend', { params: { path: { id: letter.id } } }),
      ),
    onSuccess: () => {
      setNotice(true);
      invalidate();
    },
  });
  const abandon = useMutation({
    mutationFn: ({ letter, reason }: { letter: DeadLetterView; reason: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/dead-letters/{id}:abandon', {
          params: { path: { id: letter.id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      setAbandoning(null);
      invalidate();
    },
  });

  const columns: DataTableColumn<DeadLetterView>[] = [
    {
      id: 'time',
      headerKey: 'int:dead_letters.columns.time',
      cell: (row) => <JalaliDate value={row.deadLetteredAt} withTime />,
    },
    {
      id: 'adapter',
      headerKey: 'int:dead_letters.columns.adapter',
      cell: (row) => <AdapterName code={row.adapterCode} isSample={row.isSample} />,
    },
    {
      id: 'operation',
      headerKey: 'int:dead_letters.columns.operation',
      cell: (row) => <bdi dir="ltr">{`${row.port}.${row.operation}`}</bdi>,
    },
    {
      id: 'consumer',
      headerKey: 'int:dead_letters.columns.consumer',
      cell: (row) => <bdi dir="ltr">{row.consumerRef}</bdi>,
    },
    { id: 'attempts', headerKey: 'int:dead_letters.columns.attempts', cell: (row) => faDigits(Number(row.attempts)) },
    {
      id: 'error',
      headerKey: 'int:dead_letters.columns.error',
      cell: (row) => (
        <Stack spacing={0.25}>
          <bdi dir="ltr">{row.lastErrorCode}</bdi>
          {row.resolutionReason ? <span>{row.resolutionReason}</span> : null}
        </Stack>
      ),
    },
    {
      id: 'status',
      headerKey: 'int:dead_letters.columns.status',
      cell: (row) => (
        <span data-testid={`dead-letter-status-${row.id}`} data-status={row.status}>
          {t(`int:dead_letters.status.${row.status}`, { defaultValue: row.status })}
        </span>
      ),
    },
    {
      id: 'actions',
      headerKey: 'int:dead_letters.columns.actions',
      cell: (row) =>
        row.status === 'Open' ? (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t('int:dead_letters.resend')}>
              <span>
                <IconButton
                  aria-label={t('int:dead_letters.resend')}
                  onClick={() => resend.mutate(row)}
                  disabled={!write.canWrite || !row.canResend || resend.isPending}
                  data-testid={`dead-letter-resend-${row.id}`}
                >
                  <ReplayOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('int:dead_letters.abandon')}>
              <span>
                <IconButton
                  aria-label={t('int:dead_letters.abandon')}
                  onClick={() => setAbandoning(row)}
                  disabled={!write.canWrite}
                  data-testid={`dead-letter-abandon-${row.id}`}
                >
                  <DoNotDisturbOnOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_1002}
      titleKey="int:dead_letters.title"
      subtitleKey="int:dead_letters.subtitle"
      filters={
        <Stack spacing={1}>
          <TextField
            select
            size="small"
            label={t('int:dead_letters.filter')}
            value={status}
            onChange={(event) => setStatus(event.target.value as DeadLetterFilter)}
            sx={{ maxWidth: 220 }}
            slotProps={{ htmlInput: { 'data-testid': 'dead-letter-filter' } }}
          >
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`int:dead_letters.status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <WriteNotice write={write} />
          {resend.error ? <ErrorState error={resend.error} compact /> : null}
          {notice ? (
            <Alert severity="success" onClose={() => setNotice(false)} data-testid="dead-letter-resent">
              {t('int:dead_letters.resent')}
            </Alert>
          ) : null}
        </Stack>
      }
    >
      <DataTable
        captionKey="int:dead_letters.caption"
        columns={columns}
        rows={letters.data?.items}
        getRowId={(row) => row.id}
        isLoading={letters.isPending}
        error={letters.error}
        onRetry={() => void letters.refetch()}
        emptyTitleKey="int:dead_letters.empty"
      />
      <ReasonDialog
        open={abandoning !== null}
        titleKey="int:dead_letters.abandon_title"
        confirmKey="int:dead_letters.abandon"
        busy={abandon.isPending}
        error={abandon.error}
        onConfirm={(reason) => abandoning && abandon.mutate({ letter: abandoning, reason })}
        onCancel={() => {
          setAbandoning(null);
          abandon.reset();
        }}
      />
    </ListPage>
  );
}
